import { zodResolver } from "@hookform/resolvers/zod";
import { Info } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import styles from "./Base.module.scss";

import {
  BudgetFormData,
  useBudgetSchema,
} from "@/components/forms/budgetPlanning/BudgetFormTypes";
import Icons from "@/components/icons";
import { Alert, AlertDescription } from "@/components/ui/v2/alert";
import { Button } from "@/components/ui/v2/button";
import BudgetForm from "@/features/transactions/components/BudgetForm";
import { useChannels } from "@/lib/contexts/Channel/useChannels";
import { usePopup } from "@/lib/hooks/usePopup";
import useToast from "@/lib/hooks/useToast";

interface EditChannelCurrentMonthBudgetPopupProps {
  budget: number;
  channelId: string;
  clientId: string;
  onCancel: () => void;
  onSuccess: () => void;
}

const EditChannelCurrentMonthBudgetInner = ({
  budget,
  channelId,
  clientId,
  onCancel,
  onSuccess,
}: EditChannelCurrentMonthBudgetPopupProps) => {
  const { disableClose, enableClose } = usePopup();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { budgetSchema } = useBudgetSchema();

  const { updateCurrentMonthBudget } = useChannels();

  const methods = useForm<BudgetFormData>({
    resolver: zodResolver(budgetSchema),
    mode: "onChange",
    defaultValues: {
      budget: budget || 0,
    },
  });

  const handleSubmit = (data: BudgetFormData) => {
    updateCurrentMonthBudget.mutate(
      {
        channelId,
        payload: { budget: data.budget, clientId2: clientId },
      },
      {
        onSuccess: () => {
          showToast(
            t("success.updated", { entity: t("common.forecast") }),
            "success"
          );
          onSuccess();
        },
      }
    );
  };

  useEffect(() => {
    if (updateCurrentMonthBudget.isPending) {
      disableClose();
    } else {
      enableClose();
    }
  }, [updateCurrentMonthBudget.isPending, disableClose, enableClose]);

  const errorCode =
    updateCurrentMonthBudget.error?.response?.data?.[0]?.errorCode;

  return (
    <>
      <div className={styles.header}>
        <h3 className={styles.title}>
          {t("actions.edit", { entity: t("Current Month Budget") })}
        </h3>
        <Alert variant="default">
          <Info className="size-4" />
          <AlertDescription>
            {t("Budget changes here only affect the current month.")}
          </AlertDescription>
        </Alert>
      </div>
      <BudgetForm
        isLoading={updateCurrentMonthBudget.isPending}
        onSubmit={handleSubmit}
        methods={methods}
        label={t("common.budget")}
        error={errorCode && t(`errorCodes.${errorCode}`)}
      />
      <div className={styles.actions}>
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={updateCurrentMonthBudget.isPending}
        >
          {t("common.cancel")}
        </Button>
        <Button
          loading={updateCurrentMonthBudget.isPending}
          onClick={methods.handleSubmit(handleSubmit)}
        >
          <Icons.AddIcon fontSize={24} />
          {t("actions.save", { entity: t("common.budget") })}
        </Button>
      </div>
    </>
  );
};

export const EditChannelCurrentMonthBudgetPopup = (
  props: EditChannelCurrentMonthBudgetPopupProps
) => {
  return <EditChannelCurrentMonthBudgetInner {...props} />;
};
